import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, ResponseType } from 'axios';
import getConfig from 'next/config';
import { GenericObject } from 'core/models/platform';
import { BaseAPIResponse } from 'src/_common/models/api';
import { getReduxStore } from 'src/redux/index';
import { RootState } from '@Common/models/redux/store';
import Bugsnag from '@bugsnag/js';
import Firestore from '@FireStore';
import networkGateway, { getUserIdToken } from './gateway';
import CacheManager from '../cacheClient';
import ErrorHandler, { handleMaintenanceDialog } from './errorHandler';
import { serializeQueryParams } from '..';

const { publicRuntimeConfig } = getConfig();

let clientInstance: AxiosInstance;
let trackingClientInstance: AxiosInstance;
let renewingSession: Promise<void> | undefined;

const createAxiosInstance = () => {
  if (!clientInstance) {
    clientInstance = axios.create({
      baseURL: `${publicRuntimeConfig.QUILL_BACKEND_HOST}/api`,
      withCredentials: true,
    });

    clientInstance.interceptors.request.use(async (request: AxiosRequestConfig) => {
      if (request.url) await performSessionExpCheck(request.url);
      return request;
    });

    clientInstance.interceptors.response.use(
      (response: AxiosResponse<BaseAPIResponse>) => {
        Firestore.getInstance().setSessionFlags({
          exceeded: !!response.data?.flags?.st,
          hideManager: !!response.data?.flags?.hs,
        });
        if (response.data?.flags?.sessExT) {
          Firestore.getInstance().setSessionExpiryData(response.data?.flags?.sessExT);
        }
        if (response.data instanceof Blob) {
          return response.data;
        }
        return response.data.data;
      },
      (error) => Promise.reject(error.response?.data)
    );
  }

  if (!trackingClientInstance) {
    trackingClientInstance = axios.create({
      baseURL: `${publicRuntimeConfig.QUILL_TRACKING_HOST}/api`,
      withCredentials: true,
    });
  }

  if (
    process.browser &&
    getReduxStore()?.getState() &&
    (getReduxStore()?.getState() as RootState)?.common?.independentTool
  ) {
    clientInstance = axios.create({
      baseURL: `${window?.location?.origin}/api`,
      withCredentials: true,
    });

    clientInstance.interceptors.request.use(async (request: AxiosRequestConfig) => {
      if (request.url) await performSessionExpCheck(request.url);
      return request;
    });

    clientInstance.interceptors.response.use(
      (response: AxiosResponse<BaseAPIResponse>) => {
        Firestore.getInstance().setSessionFlags({
          exceeded: !!response.data?.flags?.st,
          hideManager: !!response.data?.flags?.hs,
        });
        if (response.data?.flags?.sessExT) {
          Firestore.getInstance().setSessionExpiryData(response.data?.flags?.sessExT);
        }
        return response.data.data;
      },
      (error) => Promise.reject(error.response?.data)
    );
  }
};

const refreshSession = async () => {
  try {
    const token = await getUserIdToken();
    if (!token) return;
    await clientInstance({
      method: 'POST',
      url: 'auth/refresh-session',
      headers: {
        useridtoken: token,
      },
    });
    renewingSession = undefined;
  } catch (error) {
    Firestore.getInstance().forceUpdateSessionExpiry();
    renewingSession = undefined;
    return;
  }
};

const performSessionExpCheck = async (url: string) => {
  if (url === 'tracking' || url === 'auth/refresh-session') return undefined;
  if (renewingSession) return renewingSession;
  if (Firestore.getInstance().isSessionValid()) return undefined;
  renewingSession = refreshSession();
  return renewingSession;
};

// The promise can be typecasted into any type of interface.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const httpGet = <T = any>(
  url: string,
  params?: Object,
  dynamicUrl?: string,
  responseType: ResponseType = 'json'
): Promise<T> =>
  new Promise((resolve, reject) => {
    createAxiosInstance();
    try {
      if ((getReduxStore()?.getState() as RootState)?.common?.maintenance?.globalMaintenance?.enabled) {
        handleMaintenanceDialog();
        return reject();
      }
    } catch (e) {
      Bugsnag.notify({
        name: 'MAINT_CHECK_ERROR',
        message: `Failed to check global maintenance state for ${url}`,
      });
    }
    if (dynamicUrl) url += `/${encodeURIComponent(dynamicUrl)}`;

    const cachedResponse = CacheManager.getInstance().get(url, params);
    if (cachedResponse) resolve(cachedResponse);
    else {
      // Constructing the axios client instance to be used later
      networkGateway((token?: string) => {
        clientInstance({
          method: 'GET',
          url,
          params,
          paramsSerializer: (paramsObj: GenericObject) => serializeQueryParams(paramsObj),
          headers: {
            useridtoken: token,
          },
          responseType,
        })
          .then((res) => {
            CacheManager.getInstance().set(url, res, params);
            resolve(res as unknown as T);
          })
          .catch((err) => {
            console.log(JSON.stringify(err));
            ErrorHandler({
              serverError: err,
              axiosInstance: clientInstance,
              replayRequest: {
                method: 'GET',
                url,
                data: params as GenericObject,
              },
            }).then(
              (res) => resolve(res as unknown as T),
              (error) => reject(error)
            );
          });
      });
    }
  });

// The promise can be typecasted into any type of interface.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const httpPost = <T = any>(url: string, data: unknown): Promise<T> =>
  new Promise((resolve, reject) => {
    createAxiosInstance();
    try {
      if ((getReduxStore()?.getState() as RootState)?.common?.maintenance?.globalMaintenance?.enabled) {
        handleMaintenanceDialog();
        return reject();
      }
    } catch (e) {
      Bugsnag.notify({
        name: 'MAINT_CHECK_ERROR',
        message: `Failed to check global maintenance state for ${url}`,
      });
    }
    const cachedResponse = CacheManager.getInstance().get(url, data as GenericObject);
    if (cachedResponse) return resolve(cachedResponse);
    let postInstance = clientInstance;
    networkGateway((token?: string) => {
      if (url === '/tracking') {
        postInstance = trackingClientInstance;
      }
      postInstance({
        method: 'POST',
        url,
        data,
        headers: {
          useridtoken: token,
        },
      })
        .then((res: any) => {
          if (res?.timedOut === false) {
            CacheManager.getInstance().set(url, res, data as GenericObject);
          }
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          ErrorHandler({
            serverError: err,
            axiosInstance: postInstance,
            replayRequest: {
              method: 'POST',
              url,
              data: data as GenericObject,
            },
          }).then(
            (res) => resolve(res as unknown as T),
            (error) => reject(error)
          );
        });
    });
  });

export const httpPut = <T = any>(url: string, data: unknown): Promise<T> =>
  new Promise((resolve, reject) => {
    createAxiosInstance();
    networkGateway((token?: string) => {
      clientInstance({
        method: 'PUT',
        url,
        data,
        headers: {
          useridtoken: token,
        },
      })
        .then((res) => resolve(res as unknown as T))
        .catch((err) => {
          console.log(JSON.stringify(err));
          ErrorHandler({
            serverError: err,
            axiosInstance: clientInstance,
            replayRequest: {
              method: 'PUT',
              url,
              data: data as GenericObject,
            },
          }).then(
            (res) => resolve(res as unknown as T),
            (error) => reject(error)
          );
        });
    });
  });

export const httpDelete = <T = any>(url: string, data: unknown): Promise<T> =>
  new Promise((resolve, reject) => {
    createAxiosInstance();
    networkGateway((token?: string) => {
      clientInstance({
        method: 'DELETE',
        url,
        data,
        headers: {
          useridtoken: token,
        },
      })
        .then((res) => resolve(res as unknown as T))
        .catch((err) => {
          console.log(JSON.stringify(err));
          ErrorHandler({
            serverError: err,
            axiosInstance: clientInstance,
            replayRequest: {
              method: 'DELETE',
              url,
              data: data as GenericObject,
            },
          }).then(
            (res) => resolve(res as unknown as T),
            (error) => reject(error)
          );
        });
    });
  });

export * from './gateway';
export * from './captchaLimitHandler';
export * from './errorHandler';
